import React, { useEffect, useState, useRef } from "react";
import { Button, Image, Modal } from "react-bootstrap";
import { useRouter } from "next/router";
import * as Yup from "yup";
import { useFormik } from "formik";
import { registerEntityUserApi } from "@/api/polls";
import { useAppDispatch, useAppSelector } from "@/lib/redux/store";
import { useToasts } from "@/hooks/toasts";
import axios from "@/lib/axios";
import { appSlice } from "@/lib/redux/slices/appSlice";
import dynamic from "next/dynamic";
import "react-phone-number-input/style.css";
import { isValidPhoneNumber } from 'libphonenumber-js';
import { useAnalytics } from "@/hooks/useAnalytics";
import { useSelector } from "react-redux";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
dayjs.extend(relativeTime);
import { useAuth } from "@/hooks/auth";
import { cleanQueryParams, getPollDemographicsRedirectFlag, setPollDemographicsRedirectFlag, clearPollDemographicsRedirectFlag } from "@/lib/utils";

const PDFViewerClient = dynamic(
    () => import("@/components/PDFViewerClient"),
    { ssr: false }
);

const PhoneNumberInput = dynamic(
    () => import("@/components/forms/PhoneNumberInput").then(mod => mod.PhoneNumberInput),
    { ssr: false }
);

function MobilePage()
{
    const router = useRouter();
    const { entity: entitySlug, hash: pollHash } = cleanQueryParams(router.query);
    const [poll, setPoll] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [apiError, setApiError] = useState(false);
    const hasRedirectedRef = useRef(false);
    const { addToast } = useToasts();
    const dispatch = useAppDispatch();
    const store = useAppSelector((state) => state.app);
    const persist = useSelector((state) => state._persist);
    const { user, roleActingAs } = store;
    const [showModal, setShowModal] = useState(false);
    const [showTermsModal, setShowTermsModal] = useState(false);
    const [termsUrl, setTermsUrl] = useState("");
    const [termsHeading, setTermsHeading] = useState("");
    const { logout } = useAuth();
    const { trackEvent } = useAnalytics();
    const [eventTracked, setEventTracked] = useState(false);

    useEffect(() =>
    {
        if (!router.isReady || !entitySlug || !pollHash || eventTracked) return;
        trackEvent("start_impression", entitySlug, pollHash);
        setEventTracked(() => true);
    }, [entitySlug, eventTracked, pollHash, router.isReady, trackEvent]);

    useEffect(() =>
    {
        if (!router.isReady || !entitySlug || !pollHash) return;
        const currentPath = router.asPath;
        if (
            hasRedirectedRef.current ||
            currentPath.includes("/verify") ||
            currentPath.includes("/poll-question")
        )
        {
            return;
        }

        const fetchPollDetails = async () =>
        {
            if (!persist?.rehydrated) return;
            setIsLoading(true);
            try
            {
                const response = await axios.get(`/api/${entitySlug}/poll/${pollHash}`);
                if (response.status === 200)
                {
                    const pollData = response.data.data;
                    setPoll(pollData);
                    hasRedirectedRef.current = true;
                    if (user && roleActingAs)
                    {
                        if (roleActingAs?.entity?.slug === entitySlug && roleActingAs?.role?.config_key === "user")
                        {
                            // If the demographics page just bounced us back here (it
                            // couldn't resolve the session), don't redirect straight
                            // back — that was the infinite loop. Show the form.
                            if (getPollDemographicsRedirectFlag())
                            {
                                clearPollDemographicsRedirectFlag();
                                return;
                            }
                            setPollDemographicsRedirectFlag();
                            router.push(`/${entitySlug}/poll/${pollHash}/demographics`);
                            return;
                        } else
                        {
                            await logout(`/${entitySlug}/poll/${pollHash}`);
                            return;
                        }
                    }
                }
            } catch (error)
            {
                const status = error?.response?.status;
                const message = error?.response?.data?.message || error.message;
                const errorCode = error?.response?.data?.error_code;
                if (status === 422)
                {
                    switch (errorCode)
                    {
                        case "e_poll_already_ended":
                            router.push(`/${entitySlug}/poll/${pollHash}/ended`);
                            break;
                        case "e_poll_already_answered":
                            router.push(`/${entitySlug}/poll/${pollHash}/completed`);
                            break;
                        case "e_poll_not_published":
                            router.push(`/errors/not-found`);
                            break;
                        case "e_poll_not_started":
                            const pollData = error?.response?.data?.data;

                            if (pollData)
                            {
                                setPoll(pollData);
                            }
                            setShowModal(true);
                            setApiError(true);
                            break;
                        default:
                            setApiError(true);
                            addToast("danger", message);
                    }
                    setApiError(true);
                } else if (status === 404)
                {
                    router.push(`/errors/not-found`);
                } else
                {
                    setApiError(true);
                    addToast("danger", "Something went wrong while fetching poll details.");
                }
            } finally
            {
                setIsLoading(false);
            }
        };

        if (!poll && !apiError && !isLoading)
        {
            fetchPollDetails();
        }
    }, [entitySlug, pollHash, poll, apiError, isLoading, addToast, router, persist?.rehydrated, user, roleActingAs, logout]);

    const registerValidationSchema = Yup.object().shape({
        mobile: Yup.string()
            .required("Phone number is required")
            .test(
                "is-valid-format",
                "Phone number is invalid or too long",
                (value) => isValidPhoneNumber(value || "")
            ),
    });

    const registerForm = useFormik({
        initialValues: { mobile: "" },
        validationSchema: registerValidationSchema,
        onSubmit: async (values) =>
        {
            try
            {
                const { parsePhoneNumberFromString } = await import("libphonenumber-js");
                const parsed = parsePhoneNumberFromString(values.mobile || "", "AU");
                if (!parsed || !parsed.isValid())
                {
                    addToast("danger", "Invalid phone number");
                    return;
                }
                const payload = { mobile: values.mobile };
                const response = await registerEntityUserApi(entitySlug, payload);
                if (response?.status === 200)
                {
                    const userEntityRoles = response.data.data.user.user_entity_roles;
                    const uer = userEntityRoles.find(
                        (item) =>
                            item.entity.slug === entitySlug &&
                            item.role.config_key === "user"
                    );

                    if (!uer)
                    {
                        addToast("danger", "You are not registered for this poll.");
                        return;
                    }
                    await dispatch({ type: "app/setUser", payload: response.data.data.user });
                    await dispatch({ type: "app/setToken", payload: response.data.data.token });
                    await dispatch({ type: "app/setIsAuthenticated", payload: true });
                    await dispatch({ type: "app/setUserEntityRoles", payload: userEntityRoles });
                    await dispatch({ type: "app/setRoleActingAs", payload: uer });
                    axios.defaults.headers.common["Authorization"] = `Bearer ${response.data.data.token}`;
                    axios.defaults.headers.common["X-Role-Acting-As"] = uer.id;
                    router.push(`/${entitySlug}/poll/${pollHash}/verify`);
                    // Clear Redux state instead of localStorage
                    dispatch(appSlice.actions.setPrivacyAccepted(false));
                    dispatch(appSlice.actions.setTermsAccepted(false));
                }
            } catch (error)
            {
                const message = error?.response?.data?.message || "Poll registration failed.";
                addToast("danger", message);
            }
        },
    });

    const privacyPolicyUrl = poll?.entity?.privacy_policy?.url?.trim() || "/pdf/Voters_Privacy_Policy.pdf";
    const termsAndConditionsUrl = poll?.entity?.terms_and_conditions?.url?.trim() || "/pdf/Voters_Terms_and_Conditions.pdf";
    const showVoterTerms = () =>
    {
        setShowTermsModal(true);
        setTermsUrl(termsAndConditionsUrl);
        setTermsHeading("Terms and Conditions");
    };
    const showVoterPrivacy = () =>
    {
        setShowTermsModal(true);
        setTermsUrl(privacyPolicyUrl);
        setTermsHeading("Privacy Policy");
    };

    return (
        <div className="d-flex justify-content-center align-items-center min-vh-100 bg-light">
            <div
                className="bg-white shadow-sm p-4 rounded pwsLogo"

            >
                <div className="text-center mb-3">
                    {poll && poll.entity?.logo && (
                        <Image
                            className="company-logo img-fluid mx-auto"
                            style={{ maxHeight: "80px", objectFit: "contain" }}
                            src={poll.entity.logo}
                            alt="Company Logo" />
                    )}
                </div>

                {/* End-date urgency — honest loss aversion */}
                {poll?.end_date && dayjs(poll.end_date).isAfter(dayjs()) && (
                    <div
                        className="text-center mb-3 py-2 px-3 rounded"
                        style={{ backgroundColor: "#fff3cd", fontSize: "13px", color: "#856404" }}
                    >
                        <i className="bi bi-clock me-1" />
                        This poll closes {dayjs(poll.end_date).fromNow()}. <br /> Make your voice heard.
                    </div>
                )}

                {/* Poll question — reciprocity: deliver value before asking for commitment */}
                {poll?.question && (
                    <h3 className="text-center fw-bold mb-3" style={{ fontSize: "1.1rem", lineHeight: 1.4 }}>
                        {poll.question}
                    </h3>
                )}

                {/* Poll media — deliver content first to build engagement */}
                {poll?.media?.url && poll.media.type === "image" && (
                    <div className="text-center mb-3">
                        <Image
                            src={poll.media.url}
                            alt="Poll image"
                            className="img-fluid rounded"
                            style={{ maxHeight: "200px", objectFit: "contain" }}
                        />
                    </div>
                )}

                {/* Benefit-oriented line */}
                <form onSubmit={registerForm.handleSubmit}>
                    <p className="text-muted text-center small mb-3" style={{ fontSize: "14px", color: "#333" }}>
                        Enter your mobile number to receive a one off verification code and cast your vote in under 30 seconds. <strong>Your response is anonymous.</strong>
                    </p>
                    <div className="mb-3">
                        <label className="form-label fw-semibold" htmlFor="mobile">
                            Mobile Number
                        </label>
                        <div className="input-group">
                            <PhoneNumberInput
                                id="mobile"
                                name="mobile"
                                className="form-control p-0"
                                form={registerForm}
                                value={registerForm.values.mobile}
                            />
                        </div>
                    </div>
                    <button
                        className="btn bg-primary-owner text-white w-100 mb-2"
                        type="submit"
                    >
                        Send Code
                    </button>

                    {/* Implicit consent — replaces blocking checkbox */}
                    <p className="text-muted text-center mb-3" style={{ fontSize: "12px", lineHeight: 1.5 }}>
                        By tapping Send Code, you agree to the{" "}
                        <span
                            onClick={showVoterTerms}
                            style={{ color: "#7b4bb7", cursor: "pointer", textDecoration: "underline" }}
                        >
                            Terms and Conditions
                        </span>{" "}
                        and{" "}
                        <span
                            onClick={showVoterPrivacy}
                            style={{ color: "#7b4bb7", cursor: "pointer", textDecoration: "underline" }}
                        >
                            Privacy Policy
                        </span>
                        .
                    </p>
                </form>

                <p className="text-muted small mb-4 text-center">
                    We value your privacy. Your mobile number will only be used for verification
                    purposes and will not be shared or stored for marketing purposes.
                </p>

                <Modal
                    show={showModal}
                    backdrop="static"
                    keyboard={false}
                    onHide={() => setShowModal(false)}
                    centered
                    className="p-4"
                >
                    <p className="text-center mt-3 mb-0 startPollsYet">
                        <i className="bi bi-hourglass-split" ></i>
                    </p>

                    <Modal.Header className="border-0 pb-0">
                        <Modal.Title className="text-center mx-auto">
                            This poll hasn&apos;t started yet
                        </Modal.Title>
                    </Modal.Header>

                    <Modal.Body className="border-0 text-center px-4 py-3 mb-3">
                        Please check back soon or wait for the scheduled start time <b> {dayjs(poll?.start_date).format('DD-MMM-YYYY hh:mma')} </b> to participate
                    </Modal.Body>
                </Modal>
                <Modal show={showTermsModal} fullscreen={true} onHide={() => setShowTermsModal(false)}>
                    <Modal.Header>
                        <Modal.Title className="w-100">
                            <div className="d-flex flex-column align-items-center">
                                <Image
                                    className="img-fluid maxWidth100 m-auto"
                                    src={poll?.entity?.logo || "/images/instant-polls-logo.jpg"}
                                    alt="Logo"
                                />
                                <h5 className="fw-bold mt-3 mb-0">{termsHeading}</h5>
                            </div>
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <div className="pdf-scroll-area">
                            <PDFViewerClient file={termsUrl} />
                        </div>
                    </Modal.Body>
                    <Modal.Footer className="d-flex justify-content-center">
                        <Button variant="primary" className="px-4" onClick={() => setShowTermsModal(false)}>
                            Close
                        </Button>
                    </Modal.Footer>
                </Modal>
            </div>
        </div>
    );
}

MobilePage.layout = "blank";
export default MobilePage;